import React from 'react';
import { Empty, Space, Table, Typography } from 'antd';
import ReportSection from './components/ReportSection';
import KeyValuePagedTable from './components/KeyValuePagedTable';
import { asString, renderPlainValue, renderValue, toHitMissTag, toSafeNumber } from './trans.effect';
import { toRrzuBasicViewModel } from './struct';
import type { RrzuBasicLogTable } from './struct';

interface Props {
  data: any;
}

const RrzuBasicTab: React.FC<Props> = ({ data }) => {
  if (!data) return <Empty description="暂无数据" />;

  const vm = toRrzuBasicViewModel(data);
  if (!vm) return <Empty description="数据解析失败" />;

  const renderSolvencyValue = (value: any) => {
    const raw = asString(value).trim();
    if (!raw) return renderPlainValue(value);

    const normalized = raw.replace(/[～〜]/g, '~').replace(/[－—]/g, '-');
    const nums = normalized.match(/-?\d+(?:\.\d+)?/g) || [];
    const isRange = (normalized.includes('~') || (normalized.includes('-') && !normalized.startsWith('-'))) && nums.length >= 2;
    if (isRange) {
      const parsed = nums.map((n) => toSafeNumber(n, NaN)).filter((n) => Number.isFinite(n));
      const maxNum = parsed.length ? Math.max(...parsed) : NaN;
      const color = maxNum <= 0 ? '#8c8c8c' : maxNum <= 1 ? '#1890ff' : maxNum <= 2 ? '#faad14' : '#f5222d';
      return <span style={{ color, fontWeight: 500 }}>{raw}</span>;
    }

    const maybeNum = nums.length ? toSafeNumber(nums[0], NaN) : NaN;
    if (Number.isFinite(maybeNum)) {
      const color = maybeNum <= 0 ? '#8c8c8c' : maybeNum <= 1 ? '#1890ff' : maybeNum <= 2 ? '#faad14' : '#f5222d';
      return <span style={{ color, fontWeight: 500 }}>{maybeNum}</span>;
    }

    const approxMap: Record<string, number> = { 无: 0, 少: 1, 中: 2, 多: 3 };
    if (raw in approxMap) {
      const num = approxMap[raw];
      const color = num <= 0 ? '#8c8c8c' : num <= 1 ? '#1890ff' : num <= 2 ? '#faad14' : '#f5222d';
      return <span style={{ color, fontWeight: 500 }}>{num}</span>;
    }

    return renderPlainValue(value);
  };

  return (
    <div style={{ padding: 20, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial' }}>
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ marginBottom: 16 }}>
          {vm.header.reportTitle || '风险报告综合版'}
        </Typography.Title>
      </div>

      <>
        <ReportSection
          title="风控结果"
          extra={
            <>
              <span>生成时间: {vm.header.createdTime || '-'}</span>
              <span>有效期至: {vm.header.validTime || '-'}</span>
            </>
          }
          bodyStyle={{ padding: 24 }}
        >
          <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
            {vm.risk.qualitativeText ? (
              <span
                style={{
                  fontSize: 24,
                  fontWeight: 'bold',
                  color: vm.risk.qualitativeColor,
                }}
              >
                {vm.risk.qualitativeText}
              </span>
            ) : (
              <span style={{ fontSize: 24, fontWeight: 'bold', color: '#d9d9d9' }}>未定性</span>
            )}

            {vm.risk.indicatorTags.length > 0 ? <Space wrap size={[8, 8]}>{vm.risk.indicatorTags}</Space> : null}
          </div>

          {vm.risk.riskResultText ? (
            <div style={{ marginBottom: 16 }}>
              <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>{vm.risk.riskResultText}</Typography.Paragraph>
            </div>
          ) : null}

          {vm.risk.riskAdviseText ? (
            <div>
              <Typography.Paragraph type="secondary" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                【注意事项】{vm.risk.riskAdviseText}
              </Typography.Paragraph>
            </div>
          ) : null}
        </ReportSection>

        <ReportSection title="风控日志" bodyStyle={{ padding: 16 }}>
          {vm.logTables.map((tableData: RrzuBasicLogTable, idx: number) => {
            let columns: any[] = [];
            let dataSource: any[] = [];
            let pagination: any = { pageSize: 8, showSizeChanger: false };
            let footer: React.ReactNode = null;
            let locale: any = undefined;

            if (tableData.kind === 'inner') {
              const inner = tableData.inner;
              const fallbackKeys = ['id', 'query_item', 'court_name', 'case_no', 'case_at', 'match_degree', 'detail'];
              const keys = inner.keys.length > 0 ? inner.keys : fallbackKeys;
              const isJudicial = tableData.key === 'judicial_risk_info';
              if (isJudicial) {
                columns = [
                  { title: '风险板块', dataIndex: 'query_item', key: 'query_item', render: (val: any) => renderValue(val) },
                  { title: '法院', dataIndex: 'court_name', key: 'court_name', render: (val: any) => renderValue(val) },
                  { title: '案号', dataIndex: 'case_no', key: 'case_no', render: (val: any) => renderValue(val) },
                  { title: '立案日期', dataIndex: 'case_at', key: 'case_at', render: (val: any) => renderValue(val) },
                  { title: '匹配度', dataIndex: 'match_degree', key: 'match_degree', render: (val: any) => renderValue(val) },
                  { title: '详情', dataIndex: 'detail', key: 'detail', render: (val: any) => renderValue(val) },
                ];
                locale = { emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> };
                footer = (
                  <div style={{ marginTop: 12, fontSize: 12 }}>
                    <div style={{ color: '#f5222d' }}>注：匹配度低于85%，不一定是本人，需要进一步确认</div>
                    <div style={{ marginTop: 4 }}>
                      信息查询：
                      <a
                        href="https://wenshu.court.gov.cn/website/wenshu/181029CR4M5A62CH/index.html?"
                        target="_blank"
                        rel="noreferrer"
                        style={{ marginLeft: 8 }}
                      >
                        中国裁判文书网
                      </a>
                      <span style={{ margin: '0 6px' }}>|</span>
                      <a href="https://zxgk.court.gov.cn/" target="_blank" rel="noreferrer">
                        中国执行信息公开网
                      </a>
                    </div>
                  </div>
                );
              } else {
                columns = (inner.headers || []).map((h: any, i: number) => ({
                  title: h,
                  dataIndex: keys[i] || String(i),
                  key: keys[i] || i,
                  render: (val: any) => renderValue(val),
                }));
              }
              dataSource = inner.rows;
            } else if (tableData.kind === 'platform') {
              const columnTitleMap: Record<string, string> = {
                order_id: '订单号',
                order_status: '订单状态',
                apply_time: '申请时间',
                item_num: '商品数量',
                category: '品类',
                is_overdue: '是否逾期',
              };
              const orderedKeys = ['order_id', 'order_status', 'apply_time', 'item_num', 'category', 'is_overdue'];
              columns = orderedKeys.map((k) => ({
                title: columnTitleMap[k] || k,
                dataIndex: k,
                key: k,
                render: (val: any) => renderPlainValue(val),
              }));
              dataSource = tableData.rows.map((r: any, i: number) => ({ ...r, key: r.key ?? i }));
              locale = { emptyText: <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} /> };
            } else if (tableData.kind === 'kv') {
              const valueRender =
                tableData.title === '欺诈风险'
                  ? (v: any) => toHitMissTag(v)
                  : tableData.title === '偿还能力'
                    ? (v: any) => renderSolvencyValue(v)
                    : (v: any) => renderPlainValue(v);

              const rows = tableData.rows.map((r: any) => ({ key: r.key, label: r.label, value: r.value }));
              return (
                <div key={idx} style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, borderLeft: '4px solid #1890ff', paddingLeft: 8 }}>
                    <Typography.Text strong>{tableData.title}</Typography.Text>
                  </div>
                  <KeyValuePagedTable rows={rows} pageSize={8} valueRender={(v) => valueRender(v)} />
                </div>
              );
            } else {
              return null;
            }

            return (
              <div key={idx} style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, borderLeft: '4px solid #1890ff', paddingLeft: 8 }}>
                  <Typography.Text strong>
                    {tableData.key === 'judicial_risk_info' ? (
                      <span>
                        司法风险 <span style={{ marginLeft: 6, color: '#1890ff', fontWeight: 400 }}>（人人租）</span>
                      </span>
                    ) : (
                      tableData.title
                    )}
                  </Typography.Text>
                </div>
                <Table
                  size="small"
                  dataSource={dataSource}
                  columns={columns}
                  pagination={pagination}
                  bordered
                  rowKey={(r: any) => r.key || r.id || JSON.stringify(r)}
                  locale={locale}
                />
                {footer}
              </div>
            );
          })}

          {vm.logTables.length === 0 ? <Empty description="暂无日志数据" /> : null}
        </ReportSection>
      </>
    </div>
  );
};

export default RrzuBasicTab;
